import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { PaperAirplaneIcon, BookOpenIcon, ClockIcon, CheckIcon, XMarkIcon, QuestionMarkCircleIcon } from '@heroicons/react/24/outline';
import api from '../../utils/api';

const C = { teal:'#00B4D8', tealDark:'#0087A8', navy:'#0D1B2A', success:'#10B981', error:'#EF4444', warning:'#F59E0B', dark:'#1F2937', grey:'#6B7280', border:'#E5E7EB', bg:'#F0F7FA' };
const LESSON_ICONS = { grammar:'📚', vocabulary:'📖', reading:'📰', writing:'✍️', conversation:'💬', ielts:'🎯', business:'💼' };
const WRITING_TYPES = ['writing', 'ielts'];
const CONVO_TYPES   = ['conversation'];

function aiMsg(content, extra = {}) { return { role: 'assistant', content, ...extra }; }
function userMsg(content)            { return { role: 'user',      content }; }

// Render markdown-like formatting as React elements
function renderMd(text) {
  if (!text) return null;
  const lines = text.split('\n');
  return lines.map((line, i) => {
    // Bold: **text**
    const parts = line.split(/(\*\*[^*]+\*\*)/g).map((p, pi) => {
      if (p.startsWith('**') && p.endsWith('**')) {
        return <strong key={pi}>{p.slice(2, -2)}</strong>;
      }
      return p;
    });
    return <span key={i}>{parts}{i < lines.length - 1 ? '\n' : ''}</span>;
  });
}

// Get options for any exercise — works with any type that has options array
function getExOptions(ex) {
  if (!ex) return [];
  // Open-ended exercises always use text input, never clickable options
  const OPEN_TYPES = ['open_ended', 'free_response', 'short_answer', 'writing_task', 'speaking_task', 'speaking_prompt', 'role_play'];
  if (OPEN_TYPES.includes(ex.type)) return [];
  // If exercise has multiple options, use them
  if (ex.options && ex.options.length > 1) return ex.options;
  // Single-option = sample answer shown as hint only, not clickable — use text input
  if (ex.options && ex.options.length === 1) return [];
  // Fill-blank with distractors — build options list
  const correct = ex.correct_answer || ex.answer || '';
  if (correct && ex.distractors && ex.distractors.length > 0) {
    const opts = [correct, ...ex.distractors].filter(Boolean);
    for (let i = opts.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [opts[i], opts[j]] = [opts[j], opts[i]];
    }
    return opts;
  }
  return [];
}

function buildExercisePrompt(ex, index, total) {
  const header = `**Exercise ${index + 1} of ${total}**\n\n`;
  const opts = getExOptions(ex);
  if (opts.length > 1) {
    return header + ex.question + '\n\nChoose the correct answer:';
  }
  return header + ex.question;
}

export default function LessonPage() {
  const { type } = useParams();
  const navigate  = useNavigate();

  const [lessonId,       setLessonId]       = useState(null);
  const [exercises,      setExercises]      = useState([]);
  const [currentIdx,     setCurrentIdx]     = useState(0);
  const [awaitingAnswer, setAwaitingAnswer] = useState(false);
  const [writingMode,    setWritingMode]    = useState(false);
  const [writingText,    setWritingText]    = useState('');
  const [writingResult,  setWritingResult]  = useState(null);
  const [pendingWritingTask, setPendingWritingTask] = useState(null); // {prompt, keyPhrases, wordMin, wordMax}
  const [convoMode,      setConvoMode]      = useState(false);

  const [messages,       setMessages]       = useState([]);
  const [input,          setInput]          = useState('');
  const [loading,        setLoading]        = useState(false);
  const [lessonLoading,  setLessonLoading]  = useState(true);
  // freeQuestions kept for legacy compat but no longer a hard limit — answers come from lesson bank (free)
  const [freeQuestions,  setFreeQuestions]  = useState(999);
  const [score,          setScore]          = useState(0);
  const [wordsLearned,   setWordsLearned]   = useState([]);
  const [showComplete,   setShowComplete]   = useState(false);
  const [selectedOption, setSelectedOption] = useState(null);
  const [optionResult,   setOptionResult]   = useState(null);
  const [elapsedTime,    setElapsedTime]    = useState(0);
  const [lessonContent,  setLessonContent]  = useState(null);

  const [sideInput,    setSideInput]    = useState('');
  const [sideMessages, setSideMessages] = useState([]);
  const [sideLoading,  setSideLoading]  = useState(false);

  const chatRef    = useRef(null);
  const inputRef   = useRef(null);
  const sideRef    = useRef(null);
  const sideInputRef = useRef(null);

  useEffect(() => {
    const t = setInterval(() => setElapsedTime(s => s + 1), 1000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    chatRef.current?.scrollTo({ top: chatRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    sideRef.current?.scrollTo({ top: sideRef.current.scrollHeight, behavior: 'smooth' });
  }, [sideMessages]);

  // ── Start lesson ───────────────────────────────────────────────────────────
  useEffect(() => {
    (async () => {
      try {
        const res = await api.post('/api/student/lesson/start', { lesson_type: type });
        const lessonData  = res.data.lesson;
        const contentData = res.data.content;

        setLessonId(lessonData.id);
        setLessonContent(contentData);

        const opening = [];

        if (type === 'reading') {
          // Show passage first, then use questions/exercises as exercises
          // lesson_bank uses 'exercises' field; lesson_materials uses 'questions'
          const questions = contentData?.questions || contentData?.exercises || [];
          setExercises(questions);
          setAwaitingAnswer(questions.length > 0);

          const readingTitle = contentData?.title || contentData?.topic || 'Comprehension';
          const vocabFocus = contentData?.vocabulary_focus || [];

          let readingIntro = `📰 **Reading Lesson: ${readingTitle}**\n\n`;
          if (contentData?.language_focus && contentData.language_focus !== 'reading skill') {
            readingIntro += `📌 **Focus:** ${contentData.language_focus}\n\n`;
          }
          readingIntro += `Read the following passage carefully:\n\n`;
          readingIntro += `───────────────────────\n${contentData?.passage || ''}\n───────────────────────\n\n`;
          if (vocabFocus.length > 0) {
            readingIntro += `📖 **Key vocabulary:**\n` +
              vocabFocus.slice(0, 4).map(v => `• **${v.word}** — ${v.definition}`).join('\n') + '\n\n';
          }
          readingIntro += `Now answer ${questions.length} comprehension questions about this text.`;

          opening.push(aiMsg(readingIntro));
          if (questions.length > 0) {
            opening.push(aiMsg(buildExercisePrompt(questions[0], 0, questions.length),
              { options: questions[0].options || [] }));
          }

        } else if (WRITING_TYPES.includes(type)) {
          // Writing / IELTS — essay submission mode
          setWritingMode(true);
          setConvoMode(false);
          setExercises([]);
          setAwaitingAnswer(false);

          if (type === 'ielts') {
            // If lesson has exercises (bank-style vocab/grammar IELTS prep), use exercise mode
            const ieltsExercises = contentData?.exercises || [];
            const hasWritingPrompt = !!(contentData?.prompt);
            if (hasWritingPrompt) {
              // True writing task — essay submission mode
              opening.push(aiMsg(
                `🎯 **IELTS ${contentData?.task_type || 'Writing Task'}**\n\n` +
                `📝 **Task:**\n${contentData.prompt}\n\n` +
                `⏱️ Time limit: ${contentData?.time_limit_minutes || 40} minutes\n\n` +
                `**Marking criteria:** Task Achievement · Coherence & Cohesion · Lexical Resource · Grammatical Range & Accuracy`
              ));
              if (contentData?.useful_phrases?.length) {
                opening.push(aiMsg(
                  `💡 **Useful phrases for this task:**\n` +
                  contentData.useful_phrases.map(p => `• "${p}"`).join('\n') +
                  `\n\nType your full response in the writing area below and press **Submit Essay ✍️**`
                ));
              } else {
                opening.push(aiMsg(`Write your full response below and press **Submit Essay ✍️** when ready.`));
              }
            } else {
              // Exercise-based IELTS prep lesson (from lesson bank)
              setWritingMode(false);
              setConvoMode(false);
              setExercises(ieltsExercises);
              setAwaitingAnswer(ieltsExercises.length > 0);
              const section = contentData?.ielts_section || contentData?.language_focus || 'IELTS Practice';
              const bandTarget = contentData?.band_target ? ` · Target Band: ${contentData.band_target}` : '';
              opening.push(aiMsg(
                `🎯 **IELTS Preparation: ${contentData?.topic || contentData?.title || 'Practice'}**\n\n` +
                `📌 ${section}${bandTarget}\n\n` +
                (contentData?.content ? `${contentData.content}\n\n` : '') +
                (contentData?.common_mistakes?.length
                  ? `⚠️ **Common mistakes to avoid:**\n${contentData.common_mistakes.map(m => `• ${m}`).join('\n')}\n\n`
                  : '') +
                (contentData?.tips?.length
                  ? `💡 **Tips:**\n${contentData.tips.slice(0,2).map(t => `• ${t}`).join('\n')}\n\n`
                  : '') +
                (ieltsExercises.length > 0 ? `Now let's practise with ${ieltsExercises.length} exercises.` : '')
              ));
              if (ieltsExercises.length > 0) {
                opening.push(aiMsg(buildExercisePrompt(ieltsExercises[0], 0, ieltsExercises.length),
                  { options: ieltsExercises[0].options || [] }));
              }
            }
          } else {
            // writing — handle both lesson_bank format (instructions/key_phrases/exercises)
            // and lesson_materials format (prompt/requirements/example_phrases)
            const writingTitle    = contentData?.title || contentData?.topic || 'English Writing';
            const writingPrompt   = contentData?.prompt || contentData?.instructions || '';
            const writingExs      = contentData?.exercises || [];
            const hasExercises    = writingExs.length > 0;

            if (hasExercises && !contentData?.prompt) {
              // Exercise-based writing skills lesson (from lesson bank)
              // Save the writing task so it appears after exercises complete
              const wTask = contentData?.instructions || contentData?.task || null;
              if (wTask) {
                setPendingWritingTask({
                  prompt: wTask,
                  keyPhrases: contentData?.key_phrases || [],
                  wordMin: contentData?.word_count_min || 80,
                  wordMax: contentData?.word_count_max || 150,
                });
              }
              setWritingMode(false);
              setExercises(writingExs);
              setAwaitingAnswer(true);

              const langFocus = contentData?.language_focus || contentData?.writing_type || 'writing skills';
              let writingIntro = `✍️ **${writingTitle}**\n\n`;
              writingIntro += `📌 **Focus:** ${langFocus}\n\n`;
              if (writingPrompt) writingIntro += `📋 **Task:** ${writingPrompt}\n\n`;
              if (contentData?.key_phrases?.length) {
                writingIntro += `💡 **Key phrases:**\n` +
                  contentData.key_phrases.slice(0,6).map(p => `• "${p}"`).join('\n') + '\n\n';
              }
              if (contentData?.checklist?.length) {
                writingIntro += `✅ **Checklist:**\n` +
                  contentData.checklist.slice(0,4).map(c => `• ${c}`).join('\n') + '\n\n';
              }
              writingIntro += `Now let's practise writing skills with ${writingExs.length} exercises.`;
              opening.push(aiMsg(writingIntro));
              opening.push(aiMsg(buildExercisePrompt(writingExs[0], 0, writingExs.length),
                { options: getExOptions(writingExs[0]) }));

              // Model answer intentionally not shown upfront
            } else {
              // True writing submission mode
              const reqs = contentData?.requirements || [];
              opening.push(aiMsg(
                `✍️ **Writing Task: ${writingTitle}**\n\n` +
                `📝 **Prompt:**\n${writingPrompt}\n\n` +
                (reqs.length ? `**Requirements:**\n${reqs.map(r => `• ${r}`).join('\n')}\n\n` : '') +
                `**Word count:** ${contentData?.word_count_min || 80}–${contentData?.word_count_max || 150} words`
              ));
            }
            if (!hasExercises) {
              // Writing submission mode — show phrases then prompt to write
              const phrases = contentData?.example_phrases || contentData?.key_phrases || [];
              if (phrases.length > 0) {
                opening.push(aiMsg(
                  `💡 **Useful phrases:**\n` +
                  phrases.map(p => `• "${p}"`).join('\n') +
                  `\n\nWrite your response below and press **Submit Essay ✍️** when ready.`
                ));
              } else {
                opening.push(aiMsg(`Write your response below and press **Submit Essay ✍️** when ready.`));
              }
            }
            // Exercise mode: no submit essay prompt — exercises handle completion
          }

        } else if (CONVO_TYPES.includes(type)) {
          // Conversation — freeform roleplay
          setConvoMode(true);
          setExercises([]);
          setAwaitingAnswer(false);

          // Field names vary by generation batch — normalise them
          const convoTitle     = contentData?.title || contentData?.topic || 'English Conversation';
          const convoSituation = contentData?.situation || contentData?.scenario || contentData?.context || '';
          const convoFocus     = contentData?.language_focus || contentData?.focus || '';
          const convoObjectives = contentData?.objectives || contentData?.learning_objectives || [];

          let introText = `💬 **${convoTitle}**\n\n`;
          if (convoSituation) introText += `📋 **Situation:** ${convoSituation}\n\n`;
          if (convoFocus)     introText += `🎯 **Language focus:** ${convoFocus}\n\n`;
          introText += `👤 **Your role:** ${contentData?.role_student || 'Yourself'}\n`;
          introText += `🤖 **My role:** ${contentData?.role_ai || 'Conversation partner'}`;
          if (convoObjectives.length > 0) {
            introText += `\n\n**Learning objectives:**\n` + convoObjectives.map(o => `• ${o}`).join('\n');
          }

          opening.push(aiMsg(introText));

          // Show useful phrases if available
          const usefulPhrases = contentData?.useful_phrases || contentData?.vocabulary || [];
          if (usefulPhrases.length > 0) {
            const phraseLines = usefulPhrases.slice(0, 5).map(p => {
              if (typeof p === 'string') return `• "${p}"`;
              if (p.phrase) return `• **"${p.phrase}"** — ${p.usage || ''}`;
              return `• "${JSON.stringify(p)}"`;
            }).join('\n');
            opening.push(aiMsg(`💡 **Useful phrases for this conversation:**\n${phraseLines}`));
          }

          // Show dialogue example if available
          const dialogue = contentData?.dialogue_example || contentData?.example_dialogue || [];
          if (dialogue.length > 0) {
            const dialogueText = dialogue.map(d => `**${d.speaker || (d.role === 'ai' ? 'B' : 'A')}:** ${d.text}`).join('\n');
            opening.push(aiMsg(`📝 **Example dialogue:**\n${dialogueText}\n\nNow let's practise — I'll start!`));
          }

          // AI starts the conversation
          if (contentData?.opener) {
            opening.push(aiMsg(contentData.opener));
          } else {
            // Generate a natural opener from the situation
            const starterSituation = convoSituation || convoTitle;
            opening.push(aiMsg(`Great! Let's begin. I'll play my role — please respond naturally in English!\n\n${
              convoSituation ? `Remember: ${convoSituation}` : ''
            }`.trim()));
          }

        } else if (type === 'vocabulary') {
          // Show word list + then exercises
          const exList = contentData?.exercises || [];
          setExercises(exList);
          setAwaitingAnswer(exList.length > 0);

          const wordList = contentData?.words || [];
          opening.push(aiMsg(
            `📖 **Vocabulary Lesson: ${contentData?.topic || 'New Words'}**\n\n` +
            `Today you will learn ${wordList.length} new words:\n\n` +
            wordList.map(w => `• **${w.word}** — ${w.definition}\n  _"${w.example || ''}"_`).join('\n\n')
          ));
          if (exList.length > 0) {
            opening.push(aiMsg(
              `Now let's practise! Complete ${exList.length} exercises to reinforce your vocabulary.\n\n` +
              buildExercisePrompt(exList[0], 0, exList.length),
              { options: getExOptions(exList[0]) }
            ));
          }

        } else if (type === 'business') {
          // Business: show context + exercises
          const exList = contentData?.exercises || [];
          setExercises(exList);
          setAwaitingAnswer(exList.length > 0);

          // Derive the real business topic from the title (strip "Business English: " prefix)
          const rawTitle = contentData?.title || '';
          const businessTopic = rawTitle.replace(/^Business English:\s*/i, '').trim()
            || contentData?.language_focus
            || 'Professional Communication';

          // Build the intro — use language_focus + cultural_note + key_vocabulary + professional_phrases
          const langFocus = contentData?.language_focus || contentData?.business_context || '';
          const culturalNote = contentData?.cultural_note || '';
          const profPhrases = contentData?.professional_phrases || contentData?.meeting_phrases || [];
          const keyVocab = contentData?.key_vocabulary || [];
          const scenario = contentData?.scenario || contentData?.summary || '';

          let introText = `💼 **${rawTitle || 'Business English'}**\n\n`;
          if (langFocus) introText += `📌 **Focus:** ${langFocus}\n\n`;
          if (scenario && !rawTitle.toLowerCase().includes(scenario.toLowerCase().slice(0,20))) {
            introText += `${scenario}\n\n`;
          }
          if (keyVocab.length > 0) {
            introText += `📖 **Key Vocabulary:**\n` +
              keyVocab.slice(0,4).map(v => `• **${v.term}** — ${v.definition}`).join('\n') + '\n\n';
          }
          if (profPhrases.length > 0) {
            introText += `🗣️ **Professional Phrases:**\n` +
              profPhrases.slice(0,4).map(p => `• "${p}"`).join('\n') + '\n\n';
          }
          if (culturalNote) {
            introText += `🌍 **Note:** ${culturalNote}`;
          }

          opening.push(aiMsg(introText.trim()));
          if (contentData?.email_template) {
            opening.push(aiMsg(`📧 **Email Template Reference:**\n\n${contentData.email_template}`));
          }
          if (exList.length > 0) {
            const firstEx = exList[0];
            if (firstEx.type === 'email_writing') {
              opening.push(aiMsg(`✍️ **Task 1:** ${firstEx.prompt}\n\nType your business email response below.`));
              setWritingMode(true);
              setExercises([]);
              setAwaitingAnswer(false);
            } else {
              opening.push(aiMsg(
                `Now test your business English knowledge:\n\n` +
                buildExercisePrompt(firstEx, 0, exList.length),
                { options: getExOptions(firstEx) }
              ));
            }
          }

        } else {
          // grammar — default exercise flow
          const exList = contentData?.exercises || contentData?.questions || [];
          setExercises(exList);
          setAwaitingAnswer(exList.length > 0);

          opening.push(aiMsg(
            `📚 **Grammar Lesson: ${contentData?.topic || 'English Grammar'}**\n\n` +
            (contentData?.explanation || 'Let\'s practise together.') +
            (contentData?.examples?.length
              ? '\n\n**Examples:**\n' + contentData.examples.map(e => `• ${e}`).join('\n')
              : '')
          ));
          if (exList.length > 0) {
            opening.push(aiMsg(
              `Now complete ${exList.length} exercises:\n\n` +
              buildExercisePrompt(exList[0], 0, exList.length),
              { options: getExOptions(exList[0]) }
            ));
          } else {
            opening.push(aiMsg('Ask me anything about grammar!'));
          }
        }

        setMessages(opening);
      } catch (err) {
        const status = err?.response?.status;
        const errMsg = err?.response?.data?.error || '';
        if (status === 403) {
          navigate('/my-course');
          return;
        } else if (status === 429) {
          setMessages([aiMsg(
            errMsg.includes('Daily') || errMsg.includes('daily')
              ? `⏳ **Daily limit reached**\n\nYou've used all your lessons for today. Come back tomorrow to continue learning! 🌟`
              : `⏳ **Lesson unavailable**\n\n${errMsg || 'Please try again later.'}`
          )]);
        } else {
          setMessages([aiMsg(`Welcome to your ${type} lesson! Something went wrong loading the lesson. Please refresh and try again.`)]);
        }
      }
      setLessonLoading(false);
    })();
  }, [type]);

  const formatTime = s => `${Math.floor(s / 60).toString().padStart(2, '0')}:${(s % 60).toString().padStart(2, '0')}`;

  // ── Submit exercise answer ─────────────────────────────────────────────────
  const submitExerciseAnswer = async (answerText) => {
    if (!lessonId || currentIdx >= exercises.length) return;
    const ex = exercises[currentIdx];
    setLoading(true);
    setMessages(prev => [...prev, userMsg(answerText)]);

    try {
      const res = await api.post(`/api/student/lesson/${lessonId}/chat`, {
        type: 'exercise_answer',
        exercise_id: ex.id,
        answer: answerText,
      });
      const { is_correct, correct_answer } = res.data;
      const newScore = is_correct ? score + 1 : score;
      if (is_correct) setScore(s => s + 1);

      setMessages(prev => [...prev, aiMsg(
        is_correct
          ? `✅ **Correct!** Well done!\n\n${ex.explanation || `The answer is "${correct_answer}".`}`
          : `❌ **Not quite.**\n\nYour answer: *"${answerText}"*\nCorrect answer: **"${correct_answer}"**\n\n${ex.explanation || ''}`
      )]);

      const nextIdx = currentIdx + 1;
      setCurrentIdx(nextIdx);

      if (nextIdx >= exercises.length) {
        setAwaitingAnswer(false);
        if (pendingWritingTask) {
          // Exercises done — now show the actual writing task
          setTimeout(() => {
            setWritingMode(true);
            setMessages(prev => [...prev, aiMsg(
              `✅ **Great work on the exercises!**\n\nNow it's time to practise your writing.\n\n` +
              `✍️ **Your Task:**\n${pendingWritingTask.prompt}\n\n` +
              (pendingWritingTask.keyPhrases.length > 0
                ? `💡 **Key phrases to use:**\n${pendingWritingTask.keyPhrases.slice(0,5).map(p => `• "${p}"`).join('\n')}\n\n`
                : '') +
              `**Target:** ${pendingWritingTask.wordMin}–${pendingWritingTask.wordMax} words\n\nWrite your response below and press **Submit Essay ✍️** when ready.`
            )]);
          }, 800);
        } else {
          setTimeout(() => setShowComplete(true), 1200);
          try { await api.post(`/api/student/lesson/${lessonId}/complete`, { score: Math.round((newScore / exercises.length) * 100) }); } catch {}
        }
      } else {
        setTimeout(() => {
          const nextEx = exercises[nextIdx];
          setMessages(prev => [...prev, aiMsg(
            buildExercisePrompt(nextEx, nextIdx, exercises.length),
            { options: getExOptions(nextEx) }
          )]);
          setSelectedOption(null);
          setOptionResult(null);
        }, 600);
      }
    } catch {
      setMessages(prev => [...prev, aiMsg('Sorry, something went wrong. Please try again.')]);
    }
    setLoading(false);
  };

  // ── Submit writing/essay ───────────────────────────────────────────────────
  const submitWriting = async () => {
    if (!lessonId || !writingText.trim()) return;
    setLoading(true);
    setMessages(prev => [...prev, userMsg(writingText)]);

    try {
      const res = await api.post(`/api/student/lesson/${lessonId}/writing`, { text: writingText });
      const ev = res.data.evaluation || {};
      let feedback = '';

      if (type === 'ielts') {
        const band = ev.estimated_band || ev.score || '—';
        feedback = `🎯 **Estimated Band: ${band}**\n\n` +
          (ev.task_achievement ? `**Task Achievement (${ev.task_achievement.score || ''}):** ${ev.task_achievement.feedback || ''}\n` : '') +
          (ev.coherence_cohesion ? `**Coherence (${ev.coherence_cohesion.score || ''}):** ${ev.coherence_cohesion.feedback || ''}\n` : '') +
          (ev.lexical_resource ? `**Lexical Resource (${ev.lexical_resource.score || ''}):** ${ev.lexical_resource.feedback || ''}\n` : '') +
          (ev.grammatical_range ? `**Grammar (${ev.grammatical_range.score || ''}):** ${ev.grammatical_range.feedback || ''}\n\n` : '\n') +
          (ev.overall_feedback ? `💬 **Overall:** ${ev.overall_feedback}\n\n` : '') +
          (ev.corrections?.length ? `📝 **Corrections:**\n${ev.corrections.map(c => `• ${c}`).join('\n')}\n\n` : '') +
          (ev.improved_phrases?.length ? `✨ **Improved phrases:**\n${ev.improved_phrases.map(p => `• ${p}`).join('\n')}` : '');
      } else {
        const sc = ev.score || 70;
        feedback = `📊 **Score: ${sc}/100**\n\n` +
          (ev.overall_feedback ? `💬 ${ev.overall_feedback}\n\n` : '') +
          (ev.grammar_feedback ? `📐 **Grammar:** ${ev.grammar_feedback}\n` : '') +
          (ev.vocabulary_feedback ? `📖 **Vocabulary:** ${ev.vocabulary_feedback}\n` : '') +
          (ev.corrections?.length ? `\n📝 **Corrections:**\n${ev.corrections.map(c => `• ${c}`).join('\n')}\n` : '') +
          (ev.strengths?.length ? `\n✨ **Strengths:**\n${ev.strengths.map(s => `• ${s}`).join('\n')}` : '');
      }

      setWritingResult(ev);
      setMessages(prev => [...prev, aiMsg(feedback)]);
      try { await api.post(`/api/student/lesson/${lessonId}/complete`, { score: ev.score || ev.estimated_band || 70 }); } catch {}
      setTimeout(() => setShowComplete(true), 1500);
    } catch {
      setMessages(prev => [...prev, aiMsg('Sorry, could not evaluate your writing. Please try again.')]);
    }
    setLoading(false);
  };

  // ── Freeform chat (conversation mode / free questions) ────────────────────
  const sendFreeform = async (text) => {
    if (!lessonId) return;
    // no freeform limit — questions answered from lesson bank content, zero API cost
    setLoading(true);
    setMessages(prev => [...prev, userMsg(text)]);

    try {
      const res = await api.post(`/api/student/lesson/${lessonId}/chat`, { type: 'freeform', message: text });
      setMessages(prev => [...prev, aiMsg(res.data.response || res.data.message || 'Let\'s keep practising!')]);
      // no decrement — unlimited questions from bank
      if (res.data.new_words) setWordsLearned(prev => [...prev, ...res.data.new_words]);
    } catch {
      setMessages(prev => [...prev, aiMsg('Let me get back to you on that. Let\'s continue!')]);
    }
    setLoading(false);
  };

  // ── Sidebar mini-chat: questions don't interrupt the main exercise flow ──────
  const sendSideQuestion = async (text = sideInput.trim()) => {
    if (!text || sideLoading || !lessonId) return;
    setSideInput('');
    setSideMessages(prev => [...prev, userMsg(text)]);
    setSideLoading(true);
    try {
      const ex = exercises[currentIdx];
      const res = await api.post(`/api/student/lesson/${lessonId}/chat`, {
        type: 'freeform',
        message: text,
        exercise_id: ex?.id,
        awaiting_answer: awaitingAnswer && currentIdx < exercises.length,
      });
      setSideMessages(prev => [...prev, aiMsg(res.data.response || res.data.message || 'Keep practising!')]);
    } catch {
      setSideMessages(prev => [...prev, aiMsg('Let me think about that. Keep going! 💪')]);
    }
    setSideLoading(false);
    sideInputRef.current?.focus();
  };

  // ── Main send handler — only used in conversation (roleplay) mode ──────────
  const handleSend = async (text = input.trim()) => {
    if (!text || loading) return;
    setInput('');
    await sendFreeform(text);
    inputRef.current?.focus();
  };

  const handleOption = (opt) => {
    if (selectedOption || loading) return;
    setSelectedOption(opt);
    const ex = exercises[currentIdx];
    const isCorrect = opt.trim().toLowerCase() === (ex?.correct_answer || ex?.answer || '').trim().toLowerCase();
    setOptionResult(isCorrect ? 'correct' : 'wrong');
    setTimeout(() => submitExerciseAnswer(opt), 700);
  };

  // ── Completion screen ─────────────────────────────────────────────────────
  if (showComplete) {
    const pct      = exercises.length > 0 ? Math.round((score / exercises.length) * 100) : (writingResult?.score || writingResult?.estimated_band ? 85 : 80);
    const xpEarned = Math.max(score, 1) * 5 + 10;
    const grade    = pct >= 80 ? '🏆 Excellent!' : pct >= 60 ? '👍 Good Job!' : pct >= 40 ? '📖 Keep Practising' : '💪 Needs Review';

    return (
      <div style={{ minHeight:'100vh', background:C.bg, display:'flex', alignItems:'center', justifyContent:'center', padding:20 }}>
        <motion.div initial={{ scale:0.8, opacity:0 }} animate={{ scale:1, opacity:1 }} className="card"
          style={{ padding:48, textAlign:'center', maxWidth:500 }}>
          <div style={{ fontSize:64, marginBottom:8 }}>🎉</div>
          <h2 style={{ margin:'0 0 4px', fontSize:26, fontWeight:800, color:C.dark }}>Lesson Complete!</h2>
          <p style={{ color:C.grey, marginBottom:24 }}>{grade}</p>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(min(100%, 200px), 1fr))', gap:12, marginBottom:24 }}>
            {[
              { val: exercises.length > 0 ? `${score}/${exercises.length}` : writingResult?.estimated_band ? `Band ${writingResult.estimated_band}` : `${writingResult?.score || pct}%`, label:'Score', color:C.success },
              { val:`${pct}%`, label:'Accuracy', color:C.teal },
              { val:`+${xpEarned}`, label:'XP Earned', color:'#8B5CF6' },
            ].map(({ val, label, color }) => (
              <div key={label} style={{ padding:16, background:'#F9FAFB', borderRadius:10 }}>
                <div style={{ fontSize:26, fontWeight:800, color }}>{val}</div>
                <div style={{ fontSize:12, color:C.grey }}>{label}</div>
              </div>
            ))}
          </div>
          {wordsLearned.length > 0 && (
            <div style={{ marginBottom:20, textAlign:'left' }}>
              <div style={{ fontSize:13, fontWeight:600, marginBottom:8 }}>Words added to your vocabulary bank:</div>
              <div style={{ display:'flex', flexWrap:'wrap', gap:6 }}>
                {wordsLearned.map(w => <span key={w} className="badge" style={{ background:'#CFFAFE', color:'#164E63' }}>{w}</span>)}
              </div>
            </div>
          )}
          <div style={{ display:'flex', gap:12 }}>
            <button onClick={() => navigate('/dashboard')}
              style={{ flex:1, padding:12, background:'#F3F4F6', border:`1px solid ${C.border}`, borderRadius:10, fontWeight:600, cursor:'pointer' }}>
              Back to Dashboard
            </button>
            <button onClick={() => window.location.reload()} className="btn-teal" style={{ flex:1, padding:12 }}>
              Next Lesson →
            </button>
          </div>
        </motion.div>
      </div>
    );
  }

  const isWritingLesson = writingMode;
  const wordCount = writingText.trim().split(/\s+/).filter(Boolean).length;

  return (
    <div style={{ height:'100vh', display:'flex', background:C.bg }}>

      {/* Left Sidebar */}
      <div style={{ width:260, background:C.navy, display:'flex', flexDirection:'column', padding:20, flexShrink:0, overflowY:'auto' }}>
        <button onClick={() => navigate('/dashboard')}
          style={{ background:'rgba(255,255,255,0.08)', border:'none', color:'rgba(255,255,255,0.7)', padding:'8px 12px', borderRadius:8, fontSize:13, cursor:'pointer', textAlign:'left', marginBottom:20 }}>
          ← Back to Dashboard
        </button>

        <div style={{ marginBottom:20 }}>
          <div style={{ fontSize:32, marginBottom:6 }}>{LESSON_ICONS[type] || '📚'}</div>
          <div style={{ color:'#fff', fontWeight:700, fontSize:17, marginBottom:2, textTransform:'capitalize' }}>{type} Lesson</div>
          <div style={{ color:'rgba(255,255,255,0.5)', fontSize:12 }}>AI-powered lesson</div>
        </div>

        {/* Progress (exercises only) */}
        {exercises.length > 0 && (
          <div style={{ marginBottom:20 }}>
            <div style={{ display:'flex', justifyContent:'space-between', marginBottom:6 }}>
              <span style={{ color:'rgba(255,255,255,0.7)', fontSize:12 }}>Progress</span>
              <span style={{ color:'#fff', fontSize:12, fontWeight:700 }}>
                {Math.min(currentIdx + 1, exercises.length)}/{exercises.length}
              </span>
            </div>
            <div style={{ height:5, background:'rgba(255,255,255,0.1)', borderRadius:3 }}>
              <div style={{ height:5, background:C.teal, borderRadius:3,
                width:`${(currentIdx / exercises.length) * 100}%`, transition:'width 0.5s ease' }} />
            </div>
            <div style={{ marginTop:6, display:'flex', gap:3, flexWrap:'wrap' }}>
              {exercises.slice(0, currentIdx).map((_, i) => (
                <div key={i} style={{ flex:1, minWidth:4, height:3, background:C.success, borderRadius:2 }} />
              ))}
              {currentIdx < exercises.length && (
                <div style={{ flex:1, minWidth:4, height:3, background:C.teal, borderRadius:2 }} />
              )}
              {Array.from({ length: Math.max(0, exercises.length - currentIdx - 1) }).map((_, i) => (
                <div key={i} style={{ flex:1, minWidth:4, height:3, background:'rgba(255,255,255,0.15)', borderRadius:2 }} />
              ))}
            </div>
          </div>
        )}

        {/* Writing word count */}
        {isWritingLesson && (
          <div style={{ background:'rgba(255,255,255,0.06)', borderRadius:8, padding:12, marginBottom:16 }}>
            <div style={{ color:'rgba(255,255,255,0.5)', fontSize:11, marginBottom:4 }}>Word Count</div>
            <div style={{ color: wordCount >= (lessonContent?.word_count_min || 80) ? C.success : '#fff', fontWeight:700, fontSize:22 }}>
              {wordCount}
            </div>
            <div style={{ color:'rgba(255,255,255,0.4)', fontSize:11 }}>
              Target: {lessonContent?.word_count_min || 80}–{lessonContent?.word_count_max || 150} words
            </div>
          </div>
        )}

        {/* Stats */}
        <div style={{ display:'flex', gap:10, marginBottom:16 }}>
          {exercises.length > 0 && (
            <div style={{ flex:1, background:'rgba(255,255,255,0.06)', borderRadius:8, padding:'10px' }}>
              <CheckIcon style={{ width:13, height:13, color:C.success, marginBottom:3 }} />
              <div style={{ color:'#fff', fontWeight:700, fontSize:15 }}>{score}</div>
              <div style={{ color:'rgba(255,255,255,0.5)', fontSize:10 }}>Correct</div>
            </div>
          )}
          <div style={{ flex:1, background:'rgba(255,255,255,0.06)', borderRadius:8, padding:'10px' }}>
            <ClockIcon style={{ width:13, height:13, color:'rgba(255,255,255,0.5)', marginBottom:3 }} />
            <div style={{ color:'#fff', fontWeight:700, fontSize:15 }}>{formatTime(elapsedTime)}</div>
            <div style={{ color:'rgba(255,255,255,0.5)', fontSize:10 }}>Time</div>
          </div>
        </div>

        {/* Ask anything — sidebar mini-chat (separate from exercise flow) */}
        {!convoMode && !isWritingLesson && (
          <div style={{ background:'rgba(0,180,216,0.07)', border:'1px solid rgba(0,180,216,0.25)', borderRadius:10, marginBottom:14, display:'flex', flexDirection:'column', overflow:'hidden' }}>
            {/* Header */}
            <div style={{ display:'flex', alignItems:'center', gap:6, padding:'8px 10px', borderBottom:'1px solid rgba(0,180,216,0.15)' }}>
              <QuestionMarkCircleIcon style={{ width:13, height:13, color:C.teal, flexShrink:0 }} />
              <div style={{ color:C.teal, fontWeight:700, fontSize:11 }}>Ask anything</div>
              <div style={{ color:'rgba(255,255,255,0.35)', fontSize:10, marginLeft:'auto' }}>free · unlimited</div>
            </div>
            {/* Mini messages */}
            {sideMessages.length > 0 && (
              <div ref={sideRef} style={{ maxHeight:180, overflowY:'auto', padding:'8px 10px', display:'flex', flexDirection:'column', gap:6 }}>
                {sideMessages.map((m, i) => (
                  <div key={i} style={{
                    fontSize:11, lineHeight:1.5,
                    padding:'6px 8px', borderRadius:8,
                    background: m.role === 'user' ? C.teal : 'rgba(255,255,255,0.07)',
                    color: m.role === 'user' ? '#fff' : 'rgba(255,255,255,0.85)',
                    alignSelf: m.role === 'user' ? 'flex-end' : 'flex-start',
                    maxWidth:'90%',
                    whiteSpace:'pre-wrap',
                  }}>
                    {m.role === 'assistant' ? renderMd(m.content) : m.content}
                  </div>
                ))}
                {sideLoading && (
                  <div style={{ display:'flex', gap:3, padding:'6px 8px' }}>
                    {[0,1,2].map(d => (
                      <motion.div key={d} style={{ width:5, height:5, borderRadius:'50%', background:'rgba(255,255,255,0.4)' }}
                        animate={{ y:[0,-4,0] }} transition={{ duration:0.5, repeat:Infinity, delay:d*0.15 }} />
                    ))}
                  </div>
                )}
              </div>
            )}
            {sideMessages.length === 0 && (
              <div style={{ padding:'6px 10px 4px', color:'rgba(255,255,255,0.4)', fontSize:10, lineHeight:1.4 }}>
                Ask why an answer is correct, request examples, or explain a grammar rule.
              </div>
            )}
            {/* Input */}
            <div style={{ display:'flex', gap:4, padding:'6px 8px', borderTop:'1px solid rgba(0,180,216,0.12)' }}>
              <input
                ref={sideInputRef}
                value={sideInput}
                onChange={e => setSideInput(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); sendSideQuestion(); } }}
                placeholder="Type your question…"
                disabled={sideLoading || !lessonId}
                style={{ flex:1, background:'rgba(255,255,255,0.06)', border:'1px solid rgba(0,180,216,0.2)', borderRadius:6,
                  padding:'5px 8px', color:'#fff', fontSize:11, outline:'none', fontFamily:'inherit' }}
              />
              <button
                onClick={() => sendSideQuestion()}
                disabled={!sideInput.trim() || sideLoading}
                style={{ width:28, height:28, borderRadius:6, background: sideInput.trim() ? C.teal : 'rgba(255,255,255,0.08)',
                  border:'none', cursor: sideInput.trim() ? 'pointer' : 'default',
                  display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                <PaperAirplaneIcon style={{ width:12, height:12, color: sideInput.trim() ? '#fff' : 'rgba(255,255,255,0.3)' }} />
              </button>
            </div>
          </div>
        )}

        {convoMode && (
          <div style={{ background:'rgba(0,180,216,0.15)', borderRadius:8, padding:10, marginBottom:14 }}>
            <div style={{ color:C.teal, fontWeight:600, fontSize:12 }}>💬 Roleplay Mode</div>
            <div style={{ color:'rgba(255,255,255,0.5)', fontSize:11, marginTop:4 }}>
              Respond naturally in English. The AI will correct mistakes gently.
            </div>
          </div>
        )}

        {wordsLearned.length > 0 && (
          <div>
            <div style={{ color:'rgba(255,255,255,0.7)', fontSize:11, fontWeight:600, marginBottom:6, textTransform:'uppercase', letterSpacing:'0.05em' }}>
              Words Learned ({wordsLearned.length})
            </div>
            <div style={{ display:'flex', flexWrap:'wrap', gap:4 }}>
              {wordsLearned.map(w => (
                <span key={w} style={{ background:'rgba(0,180,216,0.2)', color:C.teal, padding:'2px 7px', borderRadius:20, fontSize:11 }}>{w}</span>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Main Chat Area */}
      <div style={{ flex:1, display:'flex', flexDirection:'column', overflow:'hidden' }}>
        {/* Header */}
        <div style={{ background:'#fff', borderBottom:`1px solid ${C.border}`, padding:'14px 20px', display:'flex', alignItems:'center', gap:12, flexShrink:0 }}>
          <div style={{ width:36, height:36, borderRadius:'50%', background:C.teal+'18', display:'flex', alignItems:'center', justifyContent:'center', fontSize:18 }}>🤖</div>
          <div>
            <div style={{ fontWeight:700, fontSize:14 }}>SpeakIQ AI Practice</div>
            <div style={{ fontSize:12, color:C.success }}>● Online</div>
          </div>
          {awaitingAnswer && currentIdx < exercises.length && (
            <div style={{ marginLeft:'auto', background:C.teal+'18', color:C.teal, padding:'4px 12px', borderRadius:20, fontSize:12, fontWeight:600 }}>
              ✏️ Exercise {currentIdx + 1}/{exercises.length}
            </div>
          )}
          {convoMode && (
            <div style={{ marginLeft:'auto', background:'#F0FDF4', color:'#166534', padding:'4px 12px', borderRadius:20, fontSize:12, fontWeight:600 }}>
              💬 Conversation Roleplay
            </div>
          )}
          {isWritingLesson && (
            <div style={{ marginLeft:'auto', background:'#FEF9C3', color:'#854D0E', padding:'4px 12px', borderRadius:20, fontSize:12, fontWeight:600 }}>
              ✍️ Writing Task
            </div>
          )}
        </div>

        {/* Messages */}
        <div ref={chatRef} style={{ flex:1, overflowY:'auto', padding:20, display:'flex', flexDirection:'column', gap:12 }}>
          <AnimatePresence>
            {lessonLoading ? (
              <div style={{ display:'flex', gap:8 }}>
                <div style={{ width:36, height:36, borderRadius:'50%', background:'#E5E7EB', flexShrink:0 }} />
                <div className="skeleton" style={{ height:80, flex:1, maxWidth:400, borderRadius:12 }} />
              </div>
            ) : messages.map((msg, i) => (
              <motion.div key={i} initial={{ opacity:0, y:10 }} animate={{ opacity:1, y:0 }} transition={{ duration:0.2 }}
                style={{ display:'flex', flexDirection: msg.role==='user' ? 'row-reverse':'row', gap:10, alignItems:'flex-start' }}>
                {msg.role === 'assistant' && (
                  <div style={{ width:32, height:32, borderRadius:'50%', background:C.navy, display:'flex', alignItems:'center', justifyContent:'center', fontSize:14, flexShrink:0 }}>🤖</div>
                )}
                <div style={{
                  maxWidth:'75%', padding:'12px 16px',
                  borderRadius: msg.role==='user' ? '18px 18px 4px 18px' : '18px 18px 18px 4px',
                  background: msg.role==='user' ? C.teal : '#fff',
                  color: msg.role==='user' ? '#fff' : C.dark,
                  fontSize:14, lineHeight:1.7,
                  boxShadow: msg.role==='user' ? 'none' : '0 1px 3px rgba(0,0,0,0.08)',
                  border: msg.role==='user' ? 'none' : `1px solid ${C.border}`,
                  whiteSpace:'pre-wrap',
                }}>
                  {renderMd(msg.content)}
                  {msg.options?.length > 0 && (
                    <div style={{ display:'flex', flexDirection:'column', gap:8, marginTop:12 }}>
                      {msg.options.map((opt, j) => {
                        const isSelected = selectedOption === opt;
                        const ex = exercises[currentIdx];
                        const isRightAnswer = !!selectedOption && opt.trim().toLowerCase() === (ex?.correct_answer || ex?.answer || '').trim().toLowerCase();
                        let bg='#F3F4F6', color=C.dark, border=`1px solid ${C.border}`;
                        if (isSelected) {
                          bg    = optionResult==='correct' ? '#D1FAE5' : '#FEE2E2';
                          color = optionResult==='correct' ? '#065F46' : '#991B1B';
                          border= `1px solid ${optionResult==='correct' ? C.success : C.error}`;
                        } else if (isRightAnswer && optionResult === 'wrong') {
                          // Show the correct answer in green when the student picked wrong
                          bg     = '#D1FAE5';
                          color  = '#065F46';
                          border = `1px solid ${C.success}`;
                        }
                        return (
                          <button key={j} onClick={() => handleOption(opt)}
                            disabled={!!selectedOption || loading}
                            style={{ padding:'10px 16px', background:bg, border, borderRadius:8, textAlign:'left',
                              cursor: selectedOption||loading ? 'default' : 'pointer',
                              fontSize:14, color, transition:'all 0.2s', display:'flex', alignItems:'center', gap:8 }}>
                            {isSelected && (optionResult==='correct'
                              ? <CheckIcon style={{ width:16, height:16, color:C.success, flexShrink:0 }} />
                              : <XMarkIcon style={{ width:16, height:16, color:C.error, flexShrink:0 }} />
                            )}
                            {!isSelected && isRightAnswer && optionResult === 'wrong' && (
                              <CheckIcon style={{ width:16, height:16, color:C.success, flexShrink:0 }} />
                            )}
                            {opt}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              </motion.div>
            ))}
            {loading && (
              <motion.div initial={{ opacity:0 }} animate={{ opacity:1 }} style={{ display:'flex', gap:10, alignItems:'flex-start' }}>
                <div style={{ width:32, height:32, borderRadius:'50%', background:C.navy, display:'flex', alignItems:'center', justifyContent:'center', fontSize:14 }}>🤖</div>
                <div style={{ padding:'14px 18px', background:'#fff', borderRadius:'18px 18px 18px 4px', border:`1px solid ${C.border}` }}>
                  <div style={{ display:'flex', gap:4 }}>
                    {[0,1,2].map(d => (
                      <motion.div key={d} style={{ width:8, height:8, borderRadius:'50%', background:C.grey }}
                        animate={{ y:[0,-6,0] }} transition={{ duration:0.6, repeat:Infinity, delay:d*0.2 }} />
                    ))}
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Input area */}
        <div style={{ background:'#fff', borderTop:`1px solid ${C.border}`, padding:'14px 20px', flexShrink:0 }}>
          {isWritingLesson ? (
            // Writing textarea + submit button
            <div>
              <textarea
                value={writingText}
                onChange={e => setWritingText(e.target.value)}
                placeholder="Write your response here..."
                rows={5}
                disabled={loading || !!writingResult}
                style={{ width:'100%', padding:'12px 16px', border:`2px solid ${C.border}`, borderRadius:12,
                  fontSize:14, resize:'vertical', outline:'none', fontFamily:'inherit', lineHeight:1.6,
                  boxSizing:'border-box', opacity: writingResult ? 0.6 : 1 }}
              />
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginTop:8 }}>
                <span style={{ fontSize:12, color: wordCount >= (lessonContent?.word_count_min || 80) ? C.success : C.grey }}>
                  {wordCount} words {lessonContent?.word_count_min && `(min ${lessonContent.word_count_min})`}
                </span>
                {!writingResult && (
                  <button
                    onClick={submitWriting}
                    disabled={loading || !writingText.trim()}
                    className="btn-teal"
                    style={{ padding:'10px 24px', fontSize:14, opacity: !writingText.trim() ? 0.5 : 1 }}>
                    {loading ? 'Evaluating...' : 'Submit Essay ✍️'}
                  </button>
                )}
                {writingResult && (
                  <button onClick={() => setShowComplete(true)} className="btn-teal" style={{ padding:'10px 20px' }}>
                    View Summary →
                  </button>
                )}
              </div>
            </div>
          ) : (
            // Conversation mode — full chat input
            convoMode ? (
              <div>
                <div style={{ fontSize:11, color:C.grey, marginBottom:6 }}>💬 Continue the conversation in English</div>
                <div style={{ display:'flex', gap:10, alignItems:'flex-end' }}>
                  <textarea
                    ref={inputRef}
                    value={input}
                    onChange={e => setInput(e.target.value)}
                    onKeyDown={e => { if (e.key==='Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
                    placeholder="Respond in English..."
                    rows={1}
                    disabled={loading}
                    style={{ flex:1, padding:'12px 16px', border:`2px solid ${C.border}`, borderRadius:12,
                      fontSize:14, resize:'none', outline:'none', fontFamily:'inherit', lineHeight:1.5 }}
                  />
                  <motion.button whileHover={{ scale:1.05 }} whileTap={{ scale:0.95 }}
                    onClick={() => handleSend()}
                    disabled={!input.trim() || loading}
                    style={{ width:46, height:46, borderRadius:12, background:input.trim() ? C.teal : '#E5E7EB',
                      border:'none', cursor:input.trim() ? 'pointer' : 'default',
                      display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0, transition:'background 0.2s' }}>
                    <PaperAirplaneIcon style={{ width:18, height:18, color:input.trim() ? '#fff' : C.grey }} />
                  </motion.button>
                </div>
              </div>
            ) : (
              // Exercise mode — text input for fill_blank, click for multiple choice
              (() => {
                const curEx = exercises[currentIdx];
                const curExOptions = curEx ? getExOptions(curEx) : [];
                const isFillBlank = awaitingAnswer && currentIdx < exercises.length &&
                  curExOptions.length === 0 &&
                  curEx?.type !== 'role_play' && curEx?.type !== 'speaking_task' && curEx?.type !== 'speaking_prompt';
                if (isFillBlank) {
                  return (
                    <div>
                      <div style={{ fontSize:11, color:C.grey, marginBottom:6 }}>✏️ Type your answer below</div>
                      <div style={{ display:'flex', gap:10, alignItems:'flex-end' }}>
                        <input
                          ref={inputRef}
                          value={input}
                          onChange={e => setInput(e.target.value)}
                          onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); if (input.trim()) { submitExerciseAnswer(input.trim()); setInput(''); } } }}
                          placeholder="Type your answer here..."
                          disabled={loading || !!selectedOption}
                          style={{ flex:1, padding:'12px 16px', border:`2px solid ${C.teal}`, borderRadius:12,
                            fontSize:14, outline:'none', fontFamily:'inherit' }}
                        />
                        <motion.button whileHover={{ scale:1.05 }} whileTap={{ scale:0.95 }}
                          onClick={() => { if (input.trim()) { submitExerciseAnswer(input.trim()); setInput(''); } }}
                          disabled={!input.trim() || loading || !!selectedOption}
                          style={{ width:46, height:46, borderRadius:12, background:input.trim() ? C.teal : '#E5E7EB',
                            border:'none', cursor:input.trim() ? 'pointer' : 'default',
                            display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                          <PaperAirplaneIcon style={{ width:18, height:18, color:input.trim() ? '#fff' : C.grey }} />
                        </motion.button>
                      </div>
                    </div>
                  );
                }
                return (
                  <div style={{ textAlign:'center', color:C.grey, fontSize:12, padding:'4px 0' }}>
                    {awaitingAnswer && currentIdx < exercises.length
                      ? '👆 Click an option above to answer · Use the sidebar to ask questions'
                      : '✅ Exercise complete — well done!'}
                  </div>
                );
              })()
            )
          )}
        </div>
      </div>
    </div>
  );
}
